import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/store';
import { useLazyGetMaintenanceInfoQuery, IMaintenanceInfoResponse } from '@/store/api/maintenanceApi';
import { sortMaintenanceSeverityNames, MAINTENANCE_SEVERITY_NAMES, getAllSubCategories } from '@/utils/residents/maintenance/maintenance';
import { phoneNumberRegex as sharedPhoneRegex } from '@/utils/common/regex';
import { MediaUploadItem } from '@/components/common/residents/MediaCarouselUpload';
import {
  validateMaintenanceForm,
  MaintenanceStep,
  MaintenanceValidationResult,
} from '@/utils/residents/maintenance/maintenanceValidation';

export type PermissionChoice = null | 'yes' | 'no';

export interface MaintenanceFormState {
  description: string;
  setDescription: (v: string) => void;
  location: string;
  setLocation: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  subCategory: string;
  setSubCategory: (v: string) => void;
  problem: string;
  setProblem: (v: string) => void;
  severity: string;
  setSeverity: (v: string) => void;
  phoneNumber: string;
  setPhoneNumber: (v: string) => void;
  permissionChoice: PermissionChoice;
  setPermissionChoice: (v: PermissionChoice) => void;
  // request fields previously stranded in the screens
  requestType: 'maintenance' | 'it';
  setRequestType: (v: 'maintenance' | 'it') => void;
  title: string;
  setTitle: (v: string) => void;
  hasPets: boolean | null;
  setHasPets: (v: boolean | null) => void;
  specialInstructions: string;
  setSpecialInstructions: (v: string) => void;
  timeSlots: string[];
  setTimeSlots: (v: string[]) => void;
  mediaItems: MediaUploadItem[];
  setMediaItems: (v: MediaUploadItem[]) => void;
  // unified validation
  validate: (step?: MaintenanceStep) => MaintenanceValidationResult;
  // touched flags
  locationTouched: boolean;
  setLocationTouched: (v: boolean) => void;
  problemTouched: boolean;
  setProblemTouched: (v: boolean) => void;
  categoryTouched: boolean;
  setCategoryTouched: (v: boolean) => void;
  subCategoryTouched: boolean;
  setSubCategoryTouched: (v: boolean) => void;
  severityTouched: boolean;
  setSeverityTouched: (v: boolean) => void;
  phoneTouched: boolean;
  setPhoneTouched: (v: boolean) => void;
  // options
  maintenanceInfo?: IMaintenanceInfoResponse;
  locationOptions: string[];
  categoryOptions: string[];
  subCategoryOptions: string[];
  problemOptions: string[];
  severityOptions: string[];
  // validation
  isFormValid: boolean;
  isLocationMissing: boolean;
  isProblemMissing: boolean;
  isCategoryMissing: boolean;
  isSubCategoryMissing: boolean;
  // required flags (mirror UI required indicators)
  isLocationRequired: boolean;
  isProblemRequired: boolean;
  isCategoryRequired: boolean;
  isSubCategoryRequired: boolean;
  phoneNumberRegex: RegExp;
  // api loading
  loadInfo: () => void;
  /** True while the maintenance-info lazy query is uninitialized or fetching. */
  isInfoLoading: boolean;
  // error exposure for UI fallbacks
  isInfoError: boolean;
  infoErrorStatus?: number;
}

export const useMaintenanceForm = (): MaintenanceFormState => {
  const authUser = useAppSelector((s) => s.auth.user as any);
  const [getInfo, { data: infoData, isUninitialized, isFetching, isError, error }] = useLazyGetMaintenanceInfoQuery();

  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [problem, setProblem] = useState('');
  const [severity, setSeverity] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  // iOS defaults first option to selected (permission yes)
  const [permissionChoice, setPermissionChoice] = useState<PermissionChoice>('yes');
  const [requestType, setRequestType] = useState<'maintenance' | 'it'>('maintenance');
  const [title, setTitle] = useState('');
  const [hasPets, setHasPets] = useState<boolean | null>(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [timeSlots, setTimeSlots] = useState<string[]>(['', '', '']);
  const [mediaItems, setMediaItems] = useState<MediaUploadItem[]>([]);

  const [locationTouched, setLocationTouched] = useState(false);
  const [problemTouched, setProblemTouched] = useState(false);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [subCategoryTouched, setSubCategoryTouched] = useState(false);
  const [severityTouched, setSeverityTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  useEffect(() => { if (authUser?.phoneNumber) setPhoneNumber(String(authUser.phoneNumber)); }, [authUser]);

  const maintenanceInfo: IMaintenanceInfoResponse | undefined = infoData?.response;
  const locationOptions = useMemo(() => maintenanceInfo?.locations?.map(l => l.name) || [], [maintenanceInfo]);
  const categoryOptions = useMemo(() => maintenanceInfo?.categories?.map(c => c.name) || [], [maintenanceInfo]);
  const rawSubCategoryOptions = useMemo(() => {
    const cat = maintenanceInfo?.categories?.find(c => c.name === category);
    return cat?.subCategories?.map(s => s.name) || [];
  }, [maintenanceInfo, category]);
  // Effective options: when no category is chosen yet (mobile-style flow) fall
  // back to the flattened list of every subcategory.
  const subCategoryOptions = useMemo(
    () => (category ? rawSubCategoryOptions : getAllSubCategories(maintenanceInfo)),
    [category, rawSubCategoryOptions, maintenanceInfo],
  );
  const problemOptions = useMemo(() => {
    const locId = maintenanceInfo?.locations?.find(l => l.name === location)?.id?.toString();
    const catId = maintenanceInfo?.categories?.find(c => c.name === category)?.id;
    const problems = (maintenanceInfo?.problems || [])
      .filter(p => (catId == null || p.categoryId == null || p.categoryId === catId))
      .filter(p => !p.locations || p.locations.includes(locId || ''));
    return problems.map(p => p.name);
  }, [maintenanceInfo, location, category]);
  const severityOptions = useMemo(() => {
    const apiNames = (maintenanceInfo?.priorities || []).map(p => (p as any)?.name).filter(Boolean) as string[];
    return (apiNames.length ? apiNames : MAINTENANCE_SEVERITY_NAMES).slice().sort(sortMaintenanceSeverityNames);
  }, [maintenanceInfo]);

  const phoneNumberRegex = sharedPhoneRegex;

  // A field is required only when options are present (mirrors UI required flags)
  const isLocationRequired = (locationOptions.length ?? 0) > 0;
  const isProblemRequired = (problemOptions.length ?? 0) > 0;
  const isCategoryRequired = (categoryOptions.length ?? 0) > 0;
  const isSubCategoryRequired = (subCategoryOptions.length ?? 0) > 0;

  const isFormValid = Boolean(
    (!isLocationRequired || location) &&
    (!isCategoryRequired || category) &&
    (!isProblemRequired || problem) &&
    (!isSubCategoryRequired || subCategory) &&
    severity &&
    description &&
    phoneNumber && phoneNumberRegex.test(phoneNumber) &&
    permissionChoice !== null,
  );

  const isLocationMissing = !location && locationTouched;
  const isProblemMissing = !problem && problemTouched;
  const isCategoryMissing = !category && categoryTouched;
  const isSubCategoryMissing = (subCategoryOptions.length > 0) && !subCategory && subCategoryTouched;

  const loadInfo = useCallback(() => {
    if (isUninitialized || (!isUninitialized && !infoData && !isFetching && !isError)) {
      getInfo();
    }
  }, [getInfo, isUninitialized, isFetching, infoData, isError]);

  const validate = useCallback(
    (step?: MaintenanceStep): MaintenanceValidationResult =>
      validateMaintenanceForm(
        { requestType, subCategoryOptions, subCategory, severity, title, permissionChoice, timeSlots },
        step,
      ),
    [requestType, subCategoryOptions, subCategory, severity, title, permissionChoice, timeSlots],
  );

  return {
    description, setDescription,
    location, setLocation,
    category, setCategory,
    subCategory, setSubCategory,
    problem, setProblem,
    severity, setSeverity,
    phoneNumber, setPhoneNumber,
    permissionChoice, setPermissionChoice,
    requestType, setRequestType,
    title, setTitle,
    hasPets, setHasPets,
    specialInstructions, setSpecialInstructions,
    timeSlots, setTimeSlots,
    mediaItems, setMediaItems,
    validate,
    locationTouched, setLocationTouched,
    problemTouched, setProblemTouched,
    categoryTouched, setCategoryTouched,
    subCategoryTouched, setSubCategoryTouched,
    severityTouched, setSeverityTouched,
    phoneTouched, setPhoneTouched,
    maintenanceInfo,
    locationOptions,
    categoryOptions,
    subCategoryOptions,
    problemOptions,
    severityOptions,
    isFormValid,
    isLocationMissing,
    isProblemMissing,
    isCategoryMissing,
    isSubCategoryMissing,
    isLocationRequired,
    isProblemRequired,
    isCategoryRequired,
    isSubCategoryRequired,
    phoneNumberRegex,
    loadInfo,
    isInfoLoading: isUninitialized || isFetching,
    isInfoError: Boolean(isError),
    infoErrorStatus: (error as any)?.status ?? (error as any)?.originalStatus,
  };
};


